import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { Box, Link, Typography } from '@mui/material';
import PendingChangeWrapperCDS from '@sharedCR/coreDesignSystem/PendingChangeWrapperCDS';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { TAG_WIDGET_IDS, WidgetType } from 'constants/loanApplication';
import { formatDynamicValue } from 'utils/dynamicValues';
import { getHeaderPendingChangeKind } from 'utils/pendingChangeKind';

import {
  selectAssignedUsers,
  selectIsProgramChangeRequired,
  selectLoanMilestoneStatusName,
  selectLoanNumber,
  selectPrimaryBorrowerLatestCreditReportDocumentId,
} from 'store/loanApplications/selectors';

import HeaderItemValue from '../HeaderItemValue';
import { LoanHeadersTestIds } from '../LoanHeaders.testIds';
import { getHref, getIsBlurredStatus } from '../LoanHeaders.utils';

import { itemStyles, itemTitleStyles, itemValueStyles } from './LoanInfoHeader.styles';
import { LoanInfoHeaderItemTestIds } from './LoanInfoHeaderItem.testIds';

const defaultObject = {};

const LoanInfoHeaderItem = ({
  name,
  value = null,
  dataType = null,
  id,
  normalizedName,
  applicationId,
  model = defaultObject,
  dynamicFieldName = '',
  dynamicFieldId = null,
  showLabel = true,
  hasPendingChange = false,
  changeKind = null,
}) => {
  const isProgramChangeRequired = useSelector(selectIsProgramChangeRequired);
  const latestCreditReportDocumentId = useSelector(
    selectPrimaryBorrowerLatestCreditReportDocumentId,
  );
  const assignedUsers = useSelector(selectAssignedUsers);
  const loanNumber = useSelector(selectLoanNumber);
  const milestoneStatusName = useSelector(selectLoanMilestoneStatusName);

  const isLoanRolesWidget = dynamicFieldId === WidgetType.LOAN_ROLES.id;
  const isTagWidget = TAG_WIDGET_IDS.includes(dynamicFieldId);

  const shouldShowLabel = showLabel !== false && !isTagWidget;

  const href = getHref({
    field: normalizedName,
    applicationId,
    latestCreditReportDocumentId,
  });

  const isValueBlurred = getIsBlurredStatus({
    field: normalizedName,
    isProgramChangeRequired,
  });

  const formattedValue = formatDynamicValue(
    {
      value,
      field: normalizedName,
      type: dataType,
      dynamicFieldName,
    },
    { ...model },
  );

  const pendingChangeKind = getHeaderPendingChangeKind(hasPendingChange, changeKind);

  const renderText = useCallback(
    (text, dataValue = text) => (
      <PendingChangeWrapperCDS isSmall changeKind={pendingChangeKind}>
        <Typography
          data-truncated
          className={clsx('truncate', { 'tool__blur-all': isValueBlurred })}
          data-testid={LoanHeadersTestIds.getLoanInfoHeaderItemValueId(dataValue)}
          data-value={dataValue}
          sx={itemValueStyles}
          variant="regular14"
        >
          {text}
        </Typography>
      </PendingChangeWrapperCDS>
    ),
    [pendingChangeKind, isValueBlurred],
  );

  if (isLoanRolesWidget && !assignedUsers?.length) {
    return null;
  }

  if (isTagWidget && !value) {
    return null;
  }

  const fieldContent = (
    <Box key={id} sx={itemStyles}>
      {shouldShowLabel && (
        <Typography
          data-truncated
          className="truncate"
          data-testid={LoanHeadersTestIds.getLoanInfoHeaderItemId(id)}
          sx={itemTitleStyles}
          variant="regular14"
        >
          {name}
        </Typography>
      )}
      <HeaderItemValue
        assignedUsers={assignedUsers}
        dynamicFieldId={dynamicFieldId}
        formattedValue={formattedValue}
        loanNumber={loanNumber}
        milestoneStatusName={milestoneStatusName}
        normalizedName={normalizedName}
        rawValue={value}
        renderText={renderText}
      />
    </Box>
  );

  return href ? (
    <Link
      data-testid={LoanInfoHeaderItemTestIds.getLinkId(id)}
      href={href}
      key={id}
      rel="noreferrer"
      target="_blank"
    >
      {fieldContent}
    </Link>
  ) : (
    fieldContent
  );
};

LoanInfoHeaderItem.propTypes = {
  applicationId: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  normalizedName: PropTypes.string.isRequired,
  changeKind: PropTypes.string,
  dataType: PropTypes.string,
  dynamicFieldId: PropTypes.string,
  dynamicFieldName: PropTypes.string,
  hasPendingChange: PropTypes.bool,
  model: PropTypes.object,
  showLabel: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
};

export default LoanInfoHeaderItem;
